import { computed, inject, Injectable } from '@angular/core';
import { WaterTransferDocumentServiceStore } from '@app/services/water-transfer-document/store/water-transfer-document-service.store';
import { TransactionStore } from '@app/transaction/store/transaction.store';
import { WaterFinalInvoiceServiceStore } from '@app/services/water-final-invoice/store/water-final-invoice-service.store';
import { WaterNewContractServiceStore } from '@app/services/water-new-contract/store/water-new-contract-service.store';
import { StatusBadgeStatus } from '@ui-v2/atoms/status-badge/status-badge.constants';
import { EnergyStopStore } from '@app/services/energy-stop/store/energy-stop.store';
import { EnergyStopSuppliersStatus } from '@app/services/energy-stop/enums/energy-stop-suppliers-status';
import { WaterMeterStore } from '@smooved-lib/lib/meters/store/water-meter.store';

@Injectable({
    providedIn: 'root',
})
export class ServiceBadgeService {
    private readonly transactionStore = inject(TransactionStore);
    private readonly waterTransferDocumentServiceStore = inject(WaterTransferDocumentServiceStore);
    private readonly waterFinalInvoiceServiceStore = inject(WaterFinalInvoiceServiceStore);
    private readonly waterNewContractServiceStore = inject(WaterNewContractServiceStore);
    private readonly energyStopStore = inject(EnergyStopStore);
    private readonly waterMeterStore = inject(WaterMeterStore);

    public waterMeterBadgeStatusForMover = computed(() => {
        if (this.waterMeterComplete()) return StatusBadgeStatus.Complete;
        if (!this.requiredActiveStateForWaterServiceMover()) return StatusBadgeStatus.Unknown;
        if (this.hasWaterTransferDocument()) return StatusBadgeStatus.Unknown;
        return this.movingDateTodayOrInPast() ? StatusBadgeStatus.ToComplete : StatusBadgeStatus.Unknown;
    });

    public waterMeterBadgeStatusForRealEstateAgent = computed(() => {
        if (this.waterMeterComplete()) return StatusBadgeStatus.Complete;
        return this.returnWaterBadgeStatusIncompleteForRealEstateAgent();
    });

    public waterSupplierBadgeStatusForMover = computed(() => {
        if (this.hasWaterSupplier()) return StatusBadgeStatus.Complete;
        if (!this.requiredActiveStateForWaterServiceMover()) return StatusBadgeStatus.Unknown;
        if (this.hasWaterTransferDocument()) return StatusBadgeStatus.Unknown;
        return this.movingDateTodayOrInPast() ? StatusBadgeStatus.ToComplete : StatusBadgeStatus.Unknown;
    });

    public waterSupplierBadgeStatusForRealEstateAgent = computed(() => {
        if (this.hasWaterSupplier()) return StatusBadgeStatus.Complete;
        return this.returnWaterBadgeStatusIncompleteForRealEstateAgent();
    });

    public waterNewResidentInfoBadgeStatusForMover = computed(() => {
        const isLeaver = this.transactionStore.isLeaver();
        const newResidentsInfoComplete = this.waterTransferDocumentServiceStore.waterTransferDocumentService()?.newResidentsInfoComplete();

        if (newResidentsInfoComplete) return StatusBadgeStatus.Complete;

        if (isLeaver) {
            return StatusBadgeStatus.Unknown;
        } else {
            const waterNewContractServiceActive = this.waterNewContractServiceStore.service()?.active;
            if (!waterNewContractServiceActive) return StatusBadgeStatus.Unknown;

            if (this.hasWaterTransferDocument()) return StatusBadgeStatus.Unknown;

            return this.movingDateTodayOrInPast() ? StatusBadgeStatus.ToComplete : StatusBadgeStatus.Unknown;
        }
    });

    public waterNewResidentInfoBadgeStatusForRealEstateAgent = computed(() => {
        const newResidentsInfoComplete = this.waterTransferDocumentServiceStore.waterTransferDocumentService()?.newResidentsInfoComplete();
        if (newResidentsInfoComplete) return StatusBadgeStatus.Complete;
        return this.returnWaterBadgeStatusIncompleteForRealEstateAgent();
    });

    public hasAssetWaterTransferDocumentBadgeStatusForRealEstateAgent = computed(() => {
        const complete = !!this.waterTransferDocumentServiceStore.hasAssetWaterTransferDocument();
        if (complete) return StatusBadgeStatus.Complete;
        return this.returnWaterBadgeStatusIncompleteForRealEstateAgent();
    });

    /**
     * This is always 'complete', since this is only used in the leaver environment
     */
    public leaverPresentBadgeStatusForMover = computed(() => {
        return StatusBadgeStatus.Complete;
    });

    public energyStopSupplierBadgeStatus = computed(() => {
        const energyStop = this.energyStopStore.energyStop();
        if (!energyStop) return StatusBadgeStatus.Unknown;
        const complete = energyStop.states.suppliersStatus === EnergyStopSuppliersStatus.Complete;
        if (complete) return StatusBadgeStatus.Complete;
        if (!energyStop.active) return StatusBadgeStatus.Unknown;
        return this.movingDateTodayOrInPast() ? StatusBadgeStatus.ToComplete : StatusBadgeStatus.Unknown;
    });

    private returnWaterBadgeStatusIncompleteForRealEstateAgent = computed(() => {
        const waterTransferDocumentServiceActive = this.waterTransferDocumentServiceStore.waterTransferDocumentService()?.active;
        if (!waterTransferDocumentServiceActive) return StatusBadgeStatus.Unknown;
        return StatusBadgeStatus.ToComplete;
    });

    private requiredActiveStateForWaterServiceMover = computed(() => {
        const isLeaver = this.transactionStore.isLeaver();
        const waterFinalInvoiceServiceActive = this.waterFinalInvoiceServiceStore.service()?.active;
        const waterNewContractServiceActive = this.waterNewContractServiceStore.service()?.active;
        return isLeaver ? waterFinalInvoiceServiceActive : waterNewContractServiceActive;
    });

    public energyStopMeterBadgeStatus = computed(() => {
        const energyStop = this.energyStopStore.energyStop();
        if (!energyStop) return StatusBadgeStatus.Unknown;
        if (!!energyStop.states?.meterComplete) return StatusBadgeStatus.Complete;
        if (!energyStop.active) return StatusBadgeStatus.Unknown;
        return this.movingDateTodayOrInPast() ? StatusBadgeStatus.ToComplete : StatusBadgeStatus.Unknown;
    });

    private movingDateTodayOrInPast = computed(() => {
        return this.transactionStore.movingDateTodayOrInThePast();
    });

    private waterMeterComplete = computed(() => {
        return !!this.waterMeterStore.meterNumber() && !!this.waterMeterStore.meterReading();
    });

    private hasWaterTransferDocument = computed(() => {
        return this.waterTransferDocumentServiceStore.waterTransferDocumentService()?.hasWaterTransferDocument();
    });

    private hasWaterSupplier = computed(() => {
        return !!this.waterTransferDocumentServiceStore.waterTransferDocumentService()?.supplier;
    });
}
